import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useFolder } from "src/core/contexts/FolderContext";
import { useExecuteWorkflowActionMutation } from "src/core/gql/graphql";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { useToast } from "src/core/hooks/use-toast";
import { buildFolderFilesCountRefetch } from "src/core/utils/refetchers";

export const useValidation = (
  setShowConfirmDialog: (show: boolean) => void,
  onAfterRefetch?: () => void
) => {
  const { folderFile, refetch } = useFolder();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [executeWorkflowAction] = useExecuteWorkflowActionMutation();
  const [isLoading, setIsLoading] = useState(false);

  const paths = useRequiredTenantPaths();
  const navigate = useNavigate();

  const [isValidated, setIsValidated] = useState(false);

  const handleValidation = async (actionType: "accept" | "refuse") => {
    setIsValidated(false);
    const folderFileId = folderFile.folderFile?.id;

    if (!folderFileId) {
      toast({
        description: t("global.form.fail"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      await executeWorkflowAction({
        variables: {
          actionId: actionType === "accept" ? "VALIDATE" : "REJECT",
          folderFileId: folderFileId,
        },
        refetchQueries: buildFolderFilesCountRefetch(),
        awaitRefetchQueries: true,
      });
      setIsValidated(true);

      await refetch();

      // Appeler le callback après le refetch pour changer l'onglet vers "validation"
      if (onAfterRefetch) {
        onAfterRefetch();
      }
    } catch (error) {
      toast({
        description: t("global.form.fail"),
        variant: "destructive",
      });
      console.error("Erreur lors de l'envoi en validation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsValidated(false);
    setShowConfirmDialog(false);
  };

  const handleRedirect = () => {
    setShowConfirmDialog(false);
    setIsValidated(false);
    navigate(paths.home());
  };

  return { handleValidation, handleCancel, isLoading, isValidated, handleRedirect };
};
